/**
 * Layout — composição do Header + Sidebar + área de conteúdo (LDS).
 *
 * Requisitos: 14.2, 14.3, 14.4, 14.5
 */

import { useState, type ReactNode } from 'react';
import { Header } from './Header';
import { Sidebar, DRAWER_WIDTH, DRAWER_WIDTH_COLLAPSED } from './Sidebar';
import { Breadcrumbs, type BreadcrumbItem } from './Breadcrumbs';
import styles from './Layout.module.scss';

export interface LayoutProps {
  /** Conteúdo da área principal. */
  children: ReactNode;
  /** Managed_Environment ativo. */
  managedEnvironment: string;
  /** Ambientes que o deployment pode operar. */
  authorizedEnvironments: string[];
  /** Callback ao trocar de ambiente. */
  onEnvironmentChange: (env: string) => void;
  /** Rota ativa. */
  activePath?: string;
  /** Handler de navegação. */
  onNavigate?: (path: string) => void;
  /** Breadcrumbs da página atual. */
  breadcrumbs?: BreadcrumbItem[];
  /** Display name do usuário autenticado. */
  userName?: string;
  /** Email/UPN do usuário. */
  userEmail?: string;
  /** Callback de logout. */
  onLogout?: () => void;
}

export function Layout({
  children,
  managedEnvironment,
  authorizedEnvironments,
  onEnvironmentChange,
  activePath,
  onNavigate,
  breadcrumbs = [],
  userName,
  userEmail,
  onLogout,
}: LayoutProps) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const currentWidth = sidebarCollapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH;

  return (
    <div className={styles.root}>
      <Header
        managedEnvironment={managedEnvironment}
        authorizedEnvironments={authorizedEnvironments}
        onEnvironmentChange={onEnvironmentChange}
        userName={userName}
        userEmail={userEmail}
        onLogout={onLogout}
      />
      <Sidebar
        activePath={activePath}
        onNavigate={onNavigate}
        collapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
      />

      <main
        className={styles.main}
        style={{ marginLeft: currentWidth, transition: 'margin-left 0.2s ease' }}
      >
        <div className={styles.topbar}>
          {breadcrumbs.length > 0 && (
            <div className={styles.breadcrumbs}>
              <Breadcrumbs items={breadcrumbs} onNavigate={onNavigate} />
            </div>
          )}
        </div>

        {children}
      </main>
    </div>
  );
}
